import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount,
} from '../lib/accountStorage';
import { changedFields, createDocSync, type DocBase, type DocRules, type DocSync } from '../lib/syncDoc';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import type { CoachBundle } from '../services/coachAIService';

/**
 * AI Coach — CO4 persistence (docs/AI_COACH_PLAN.md).
 *
 * Small cross-device state for the coach surfaces:
 *   - dismissedFocusUntil — the Home Focus card's snooze (was localStorage-only,
 *     so it reset per device; the legacy key is migrated once on initialize)
 *   - lastFocusShown     — which domain the Focus card last surfaced, and when
 *   - history            — Mission Studio generation provenance (what was
 *     generated where, and how much of it the user applied)
 *
 * The whole blob lives in `coach_state.data`, with a localStorage cache.
 *
 * ⚠️⚠️ Writes go through syncDoc (lib/syncDoc.ts), NOT a whole-row upsert. The
 * old upsert wrote this tab's copy of everything, and `noteFocusShown` fires by
 * itself whenever Home renders the Focus card — so a stale tab erased another
 * tab's `appliedNote`, and a lost note puts the PAID Apply button back on a
 * bundle that was already applied (MissionStudio). Now a write re-reads the row
 * and applies only what this tab changed.
 */

export type CoachGenerationSource = 'home' | 'domain' | 'weekly-review';

export interface CoachGenerationRecord {
  id: string;
  domainId: string;
  domainName: string;
  source: CoachGenerationSource;
  generatedAt: string;
  /** True when the bundle came from the offline fallback, not the model. */
  mock: boolean;
  itemCount: number;
  appliedCount?: number;
  appliedAt?: string;
}

export interface LastFocusShown {
  domainId: string;
  at: string;
}

/**
 * ⭐ THE LAST SET OF ACTIONS PER DOMAIN (owner, 2026-10-11): leaving the Actions
 * tab used to throw the proposals away, and coming back meant generating (and
 * waiting) again. Kept with the objective it was made for, so a changed objective
 * reads as "made for your previous objective".
 */
export interface KeptBundle {
  bundle: CoachBundle;
  /** The objective the bundle was generated from. */
  mission: string;
  generatedAt: string;
  /** Provenance record id (markApplied). */
  recordId: string | null;
  /** "Added to Personal: 2 habits · −120 cp", once applied. */
  appliedNote?: string;
}

/** Keep provenance bounded — the newest records win. */
export const COACH_HISTORY_LIMIT = 50;

/** Legacy per-device snooze key (pre-CO4), migrated once on initialize. */
export const LEGACY_DISMISS_KEY = 'coach-focus-dismissed-until';

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `cg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

interface CoachState {
  dismissedFocusUntil: string | null;
  lastFocusShown: LastFocusShown | null;
  history: CoachGenerationRecord[];
  lastBundles: Record<string, KeptBundle>;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** The last cloud copy this tab agreed with — see `syncBase` in userStore. */
  syncBase: DocBase<CoachDoc> | null;
  initialize: () => Promise<void>;
  /** Snooze the Home Focus card for `days` days. */
  dismissFocus: (days: number) => void;
  isFocusDismissed: () => boolean;
  /** Note which domain the Focus card is currently surfacing (no-op if unchanged). */
  noteFocusShown: (domainId: string) => void;
  /** Record a Mission Studio generation; returns the record id for markApplied. */
  recordGeneration: (
    rec: Omit<CoachGenerationRecord, 'id' | 'generatedAt' | 'appliedCount' | 'appliedAt'>
  ) => string;
  markApplied: (recordId: string, appliedCount: number) => void;
  keepBundle: (domainId: string, kept: KeptBundle) => void;
  markBundleApplied: (domainId: string, appliedNote: string) => void;
}

const COACH_TABLE = 'coach_state';

function pickData(s: CoachState) {
  return {
    dismissedFocusUntil: s.dismissedFocusUntil,
    lastFocusShown: s.lastFocusShown,
    history: s.history,
    lastBundles: s.lastBundles,
  };
}

type CoachDoc = ReturnType<typeof pickData>;

/** A cloud blob (or none) as the full document — the same reads the load always did. */
function hydrateCoach(data: unknown): CoachDoc {
  const d = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<string, unknown>;
  return {
    dismissedFocusUntil: typeof d.dismissedFocusUntil === 'string' ? d.dismissedFocusUntil : null,
    lastFocusShown: (d.lastFocusShown as LastFocusShown | null | undefined) ?? null,
    history: Array.isArray(d.history) ? (d.history as CoachGenerationRecord[]) : [],
    lastBundles: d.lastBundles && typeof d.lastBundles === 'object' ? (d.lastBundles as Record<string, KeptBundle>) : {},
  };
}

/**
 * Both sides changed the provenance list: keep every record from either, by id.
 * ⚠️ An APPLIED copy beats an unapplied one — losing `appliedAt` would put a
 * spent bundle back up for a paid Apply.
 */
function mergeHistory(_base: unknown, local: unknown, cloud: unknown): CoachGenerationRecord[] {
  const byId = new Map<string, CoachGenerationRecord>();
  for (const r of (Array.isArray(cloud) ? cloud : []) as CoachGenerationRecord[]) byId.set(r.id, r);
  for (const r of (Array.isArray(local) ? local : []) as CoachGenerationRecord[]) {
    const held = byId.get(r.id);
    byId.set(r.id, held?.appliedAt && !r.appliedAt ? held : r);
  }
  return [...byId.values()]
    .sort((a, b) => b.generatedAt.localeCompare(a.generatedAt))
    .slice(0, COACH_HISTORY_LIMIT);
}

/** Both sides changed one domain's kept bundle: the newer bundle wins; for the SAME bundle, applied wins. */
function mergeBundle(_base: unknown, local: unknown, cloud: unknown): KeptBundle | undefined {
  const l = local as KeptBundle | undefined;
  const c = cloud as KeptBundle | undefined;
  if (!l || !c) return l ?? c;
  if (l.generatedAt !== c.generatedAt) return l.generatedAt > c.generatedAt ? l : c;
  return { ...c, ...l, appliedNote: l.appliedNote ?? c.appliedNote };
}

const COACH_RULES: DocRules<CoachDoc> = {
  dismissedFocusUntil: 'max', // the later snooze
  lastFocusShown: (_b, l, c) => (((l as LastFocusShown | null)?.at ?? '') >= ((c as LastFocusShown | null)?.at ?? '') ? l : c),
  history: mergeHistory,
  lastBundles: { map: mergeBundle },
};

const coachSync: DocSync<CoachDoc> = createDocSync<CoachDoc>({
  transport: supabaseBlobTransport(COACH_TABLE),
  owner: () => useCoachStore.getState().cachedUserId,
  rules: COACH_RULES,
  hydrate: hydrateCoach,
  read: () => pickData(useCoachStore.getState()),
  readBase: () => useCoachStore.getState().syncBase,
  adopt: (doc, base) => useCoachStore.setState((state: CoachState) => ({
    ...changedFields(pickData(state), doc),
    syncBase: base,
  })),
  onError: (error) => console.error('Failed to sync coach state to Supabase:', error),
});

/** Await the pending coach write (tests). */
export function flushCoachSync(): Promise<void> {
  return coachSync.flush();
}

// A tab that comes back shows another device's Apply (lib/blobFocusRefresh.ts) —
// so the PAID Apply button retires there too.
refreshBlobOnFocus(COACH_TABLE, () => coachSync.refresh(loadedFor(() => useCoachStore.getState().initializedUserId)));

export const useCoachStore = create<CoachState>()(
  persist<CoachState, [], [], any>(
    (rawSet, get) => {
      // ⚠️ The first edit of a tab whose load never finished records its base
      // (see the same wrapper in userStore).
      const set: typeof rawSet = (...args: Parameters<typeof rawSet>) => {
        if (get().syncBase === null) rawSet({ syncBase: pickData(get()) });
        return rawSet(...args);
      };
      const sync = () => coachSync.schedule();

      return {
        dismissedFocusUntil: null,
        lastFocusShown: null,
        history: [],
        lastBundles: {},
        initializedUserId: null,
        cachedUserId: null,
        syncBase: null,

        // Load-on-login, keyed by user id. The cloud row wins for everything this
        // tab has not changed since its base (a cache that is another account's
        // is replaced outright). Afterwards, adopt the legacy per-device snooze once.
        initialize: () => dedupeInit(COACH_TABLE, () => coachSync.exclusive(async () => {
          const at = accountGeneration();
          try {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;
            if (get().initializedUserId === user.id) return;
            if (get().cachedUserId === user.id && isCloudFresh(COACH_TABLE, user.id)) {
              rawSet((state) => ({ initializedUserId: user.id, syncBase: state.syncBase ?? pickData(state) }));
            } else {
              const { data, error } = await supabase.from(COACH_TABLE).select('*');
              // An answer for an account that has since signed out is not this copy's.
              if (at !== accountGeneration()) return;
              if (error) throw error;

              const row = (data ?? [])[0];
              const cloudData = row && row.data && typeof row.data === 'object' ? row.data : null;
              const state = get();
              const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
              let unsaved = false;
              if (cacheIsOurs) unsaved = coachSync.reconcile(cloudData);
              else {
                const cloud = hydrateCoach(cloudData);
                rawSet({ ...cloud, syncBase: cloud });
              }
              coachSync.noteLoaded(row?.updated_at ?? null);
              rawSet({ initializedUserId: user.id, cachedUserId: user.id });
              markCloudFresh(COACH_TABLE, user.id);
              if (unsaved) sync();
            }

            // One-time migration of the pre-CO4 per-device snooze. Runs inside
            // the deduped init (StrictMode-safe); removeItem is idempotent.
            try {
              const legacy = localStorage.getItem(LEGACY_DISMISS_KEY);
              if (legacy) {
                localStorage.removeItem(LEGACY_DISMISS_KEY);
                const current = get().dismissedFocusUntil;
                if (legacy > new Date().toISOString() && (!current || legacy > current)) {
                  set({ dismissedFocusUntil: legacy });
                  sync();
                }
              }
            } catch { /* storage unavailable — nothing to migrate */ }
          } catch (error) {
            console.error('Failed to initialize coach state from Supabase:', error);
          }
        })),

        dismissFocus: (days) => {
          set({ dismissedFocusUntil: new Date(Date.now() + days * 86_400_000).toISOString() });
          sync();
        },

        isFocusDismissed: () => {
          const until = get().dismissedFocusUntil;
          return until !== null && until > new Date().toISOString();
        },

        noteFocusShown: (domainId) => {
          if (get().lastFocusShown?.domainId === domainId) return;
          set({ lastFocusShown: { domainId, at: new Date().toISOString() } });
          sync();
        },

        recordGeneration: (rec) => {
          const id = genId();
          set((state) => ({
            history: [
              { ...rec, id, generatedAt: new Date().toISOString() },
              ...state.history,
            ].slice(0, COACH_HISTORY_LIMIT),
          }));
          sync();
          return id;
        },

        keepBundle: (domainId, kept) => {
          set((state) => ({ lastBundles: { ...(state.lastBundles ?? {}), [domainId]: kept } }));
          sync();
        },

        markBundleApplied: (domainId, appliedNote) => {
          const kept = get().lastBundles?.[domainId];
          if (!kept) return;
          set((state) => ({ lastBundles: { ...state.lastBundles, [domainId]: { ...kept, appliedNote } } }));
          sync();
        },

        markApplied: (recordId, appliedCount) => {
          set((state) => ({
            history: state.history.map((r) =>
              r.id === recordId ? { ...r, appliedCount, appliedAt: new Date().toISOString() } : r
            ),
          }));
          sync();
        },
      };
    },
    {
      name: 'coach-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state) => ({
        dismissedFocusUntil: state.dismissedFocusUntil,
        lastFocusShown: state.lastFocusShown,
        history: state.history,
        lastBundles: state.lastBundles,
        cachedUserId: state.cachedUserId,
        syncBase: state.syncBase,
      } as any),
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useCoachStore);
